import { CalendarClock, Loader2, MapPin } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@tark/shared/components';
import { useTenantFamilyBookableSlots, useTenantFamilySelfBooking } from '@tark/shared/hooks';

export function FamilyBookableOpeningsPanel({ athleteId }: { athleteId: number | null }) {
  const query = useTenantFamilyBookableSlots(athleteId, athleteId != null);
  const booking = useTenantFamilySelfBooking();
  if (!athleteId) return null;

  const groups = new Map<string, NonNullable<typeof query.data>['slots']>();
  const outsideSlots = (query.data?.slots ?? []).filter((slot) => slot.paymentPolicy === 'OFF_PLATFORM');
  outsideSlots.slice(0, 16).forEach((slot) => {
    const key = slot.startsAt.slice(0, 10);
    groups.set(key, [...(groups.get(key) ?? []), slot]);
  });

  return (
    <Card variant="default">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CalendarClock className="h-5 w-5 text-primary" />
          Open lesson times
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {query.isLoading ? (
          <div className="flex items-center gap-2 text-sm text-text-muted"><Loader2 className="h-4 w-4 animate-spin" /> Loading open times</div>
        ) : query.isError ? (
          <button type="button" className="text-sm font-semibold text-primary" onClick={() => query.refetch()}>Could not load. Retry</button>
        ) : outsideSlots.length === 0 ? (
          <p className="text-sm text-text-muted">No open times right now.</p>
        ) : Array.from(groups.entries()).map(([key, slots]) => (
          <div key={key} className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-[0.12em] text-text-subtle">
              {new Date(`${key}T12:00:00`).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}
            </p>
            {slots.map((slot) => (
              <div key={`${slot.offeringId}-${slot.startsAt}`} className="flex min-h-14 items-center gap-3 rounded-lg border border-border px-3 py-2">
                <span className="min-w-20 rounded-lg bg-primary/10 px-2 py-2 text-center text-xs font-bold text-primary">
                  {new Date(slot.startsAt).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-text">{slot.offeringTitle}</p>
                  <p className="mt-1 flex items-center gap-1 text-xs font-semibold text-primary"><MapPin className="h-3 w-3" /> In-person</p>
                  <p className="mt-1 flex items-center gap-1 truncate text-xs text-text-muted">
                    {slot.locationLabel ? <MapPin className="h-3 w-3 shrink-0" /> : null}
                    {slot.locationLabel || `${Math.round((new Date(slot.endsAt).getTime() - new Date(slot.startsAt).getTime()) / 60000)} min`}
                    {slot.priceAmount == null ? '' : ` · $${Number(slot.priceAmount).toFixed(2)}`}
                  </p>
                </div>
                <button
                  type="button"
                  className="inline-flex h-11 items-center justify-center rounded-lg bg-primary px-3 text-sm font-semibold text-on-primary disabled:opacity-60"
                  disabled={booking.isPending}
                  onClick={() => {
                    if (slot.priceAmount == null || Number(slot.priceAmount) <= 0 || slot.paymentPolicy !== 'OFF_PLATFORM') {
                      window.alert('Ask your coach to finish the price and payment setup for this lesson.');
                      return;
                    }
                    if (!window.confirm(`Book ${slot.offeringTitle} for $${Number(slot.priceAmount).toFixed(2)}?`)) return;
                    void booking.mutateAsync({
                      athleteId,
                      offeringId: slot.offeringId,
                      startsAt: slot.startsAt,
                      paymentPolicy: slot.paymentPolicy,
                    }).then((result) => {
                      if (result.checkoutUrl) {
                        window.location.assign(result.checkoutUrl);
                        return;
                      }
                      window.alert('Lesson booked. It is now on your family schedule.');
                    })
                      .catch(() => window.alert('This time was not booked. Refresh and choose another time.'));
                  }}
                >
                  Book
                </button>
              </div>
            ))}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
